// CardManager — admin assigns company cards to user accounts. A card with an
// owner is personal (only that user + Financial users see it in their receipt
// dropdown); an unowned card stays shared with everyone. This surface only
// populates owner_user_id — the visibility itself is enforced server-side
// (receipts-service formOptions). Shares the receipt module's UI vocabulary.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import useUserStore from '../stores/useUserStore';
import UserPicker from '../components/UserPicker';
import { formatCardLabel } from '../Receipts/cardLabel';
import type { CardAccount, DetectOwnerCandidate, PurchaseClassOption } from '../Receipts/types';
import PageLayout from '../components/ui/PageLayout';
import { buttonClass } from '../components/ui/Button';
import { Field } from '../components/ui/Field';
import Select from '../components/ui/Select';
import { Alert } from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import { ListRow, ListStack } from '../components/ui/ListRow';
import Panel from '../components/ui/Panel';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import Tooltip from '../components/ui/Tooltip';
import Reveal from '../components/ui/Reveal';
import FadeIn from '../components/ui/FadeIn';

interface CardRow {
  id: number;
  label: string;
  last4: string | null;
  network: string | null;
  owner_user_id: string | null;
  owner_name: string | null;
  is_active: boolean;
  default_purchase_class_id: number | null;
  default_purchase_class_label: string | null;
  account_id: number | null;        // the reconciliation account the card bills to
  account_label: string | null;
}
interface UserRow { id: string; firstname: string; lastname: string; }
interface UnmanagedCard { last4: string; account_id: number | null; txn_count: number; last_seen: string | null; }

const fieldLabel = 'block text-[11px] uppercase tracking-wider text-on-surface-variant mb-1';

const CardManager: React.FC = () => {
  const api = useApi();
  const toast = useToast();
  const user = useUserStore((s) => s.user);
  // Card management belongs to reconciliation too: financial managers manage the
  // cards their reconcile flow depends on (last4s, owners), not just admins.
  const canManage = user.isadmin || user.can_admin || user.can_financial;

  const [cards, setCards] = useState<CardRow[]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [classes, setClasses] = useState<PurchaseClassOption[]>([]);
  const [unmanaged, setUnmanaged] = useState<UnmanagedCard[]>([]);
  // Reconciliation accounts are financial-only: an admin without can_financial
  // gets a 403 here, and the roster then shows a card's account read-only.
  const [accounts, setAccounts] = useState<CardAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingOwner, setEditingOwner] = useState<number | null>(null);
  // Per-row field edit (label/last4/network) — mutually exclusive with owner edit.
  const [editingCard, setEditingCard] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState({ label: '', last4: '', network: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  // Owner detection: which row's guesses are showing, which row is mid-fetch.
  const [detectFor, setDetectFor] = useState<number | null>(null);
  const [detecting, setDetecting] = useState<number | null>(null);
  const [candidates, setCandidates] = useState<DetectOwnerCandidate[]>([]);
  const [newCard, setNewCard] = useState<{ label: string; last4: string; network: string; account_id: number | null }>({ label: '', last4: '', network: 'visa', account_id: null });
  const [busy, setBusy] = useState(false);
  const labelRef = useRef<HTMLInputElement>(null);

  const fetchCards = async () => {
    try { setCards(((await api.get('/cards')) as CardRow[]) || []); }
    catch (e: any) { toast.error(e?.message || 'Failed to load cards'); }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([
        fetchCards(),
        api.get('/users/active', { silent: true }).then((u: any) => setUsers(u || [])).catch(() => {}),
        api.get('/cards/unmanaged', { silent: true }).then((u: any) => setUnmanaged(u || [])).catch(() => {}),
        api.get('/receipts/form-options', { silent: true }).then((o: any) => setClasses(o?.classes || [])).catch(() => {}),
        api.get('/reconciliation/accounts', { silent: true }).then((a: any) => setAccounts(a || [])).catch(() => {}),
      ]);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prefill the add-card form with an unmanaged last4 (and the account its charges
  // came in on) and jump the cursor to the label.
  const setupUnmanaged = (u: UnmanagedCard) => {
    setNewCard({ label: '', last4: u.last4, network: 'visa', account_id: u.account_id ?? null });
    labelRef.current?.focus();
    labelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // Returns success so callers can hold their editor open on failure (the save
  // flow relies on this); state updates from the response row, not optimistically.
  const patchCard = async (id: number, patch: Partial<CardRow>): Promise<boolean> => {
    try {
      const updated = (await api.patch(`/cards/${id}`, { body: patch })) as CardRow;
      setCards((prev) => prev.map((c) => (c.id === id ? updated : c)));
      return true;
    } catch (e: any) { toast.error(e?.message || 'Update failed'); return false; }
  };

  const assignOwner = async (id: number, ownerId: string | null) => {
    setEditingOwner(null);
    await patchCard(id, { owner_user_id: ownerId });
  };

  // Opening one per-row editor closes the others (edit ⇔ owner-picker ⇔ detect).
  const startOwnerEdit = (id: number) => { setEditingCard(null); setDetectFor(null); setEditingOwner(id); };
  const startEdit = (c: CardRow) => {
    setEditingOwner(null);
    setDetectFor(null);
    setEditDraft({ label: c.label, last4: c.last4 || '', network: c.network || '' });
    setEditingCard(c.id);
  };
  const saveEdit = async (c: CardRow) => {
    const label = editDraft.label.trim();
    if (!label) return;
    const last4 = editDraft.last4.trim();
    const network = editDraft.network.trim();
    const patch: Partial<CardRow> = {};
    if (label !== c.label) patch.label = label;
    if ((last4 || null) !== (c.last4 || null)) patch.last4 = last4 || null;
    if ((network || null) !== (c.network || null)) patch.network = network || null;
    if (Object.keys(patch).length === 0) { setEditingCard(null); return; }
    setSavingEdit(true);
    const ok = await patchCard(c.id, patch);
    setSavingEdit(false);
    if (ok) setEditingCard(null);
  };

  // Toggle-fetch owner guesses for a card. Second click on the same row hides them.
  const runDetect = async (id: number) => {
    if (detectFor === id) { setDetectFor(null); setCandidates([]); return; }
    setDetecting(id);
    try {
      const res = (await api.get(`/cards/${id}/detect-owner`, { silent: true })) as { candidates?: DetectOwnerCandidate[] };
      setCandidates(res?.candidates || []);
      setDetectFor(id);
    } catch (e: any) { toast.error(e?.message || 'Could not detect an owner'); }
    finally { setDetecting(null); }
  };
  const applyCandidate = async (id: number, cand: DetectOwnerCandidate) => {
    const ok = await patchCard(id, { owner_user_id: cand.user_id });
    if (ok) { toast.success(`Assigned to ${cand.name}`); setDetectFor(null); setCandidates([]); }
  };
  const evidenceCaption = (e: DetectOwnerCandidate['evidence']): string => {
    const parts: string[] = [];
    if (e.labelMatch) parts.push('name matches label');
    if (e.receiptCount > 0) parts.push(`filed ${e.receiptCount} receipt${e.receiptCount === 1 ? '' : 's'} on this card`);
    return parts.join(' · ');
  };

  const addCard = async () => {
    if (!newCard.label.trim()) { toast.error('Give the card a label'); return; }
    setBusy(true);
    try {
      const created = (await api.post('/cards', { body: newCard })) as CardRow;
      setCards((prev) => [created, ...prev]);
      setUnmanaged((prev) => prev.filter((u) => u.last4 !== created.last4)); // now managed
      setNewCard({ label: '', last4: '', network: 'visa', account_id: null });
      toast.success('Card added');
    } catch (e: any) { toast.error(e?.message || 'Could not add card'); }
    finally { setBusy(false); }
  };

  const ownedCount = useMemo(() => cards.filter((c) => c.owner_user_id).length, [cards]);

  // Unmanaged last4s grouped under the account their charges came in on. Without
  // the accounts list (no can_financial) there are no names to group by.
  const unmanagedGroups = useMemo(() => {
    if (!accounts.length) return [{ label: null as string | null, rows: unmanaged }];
    const m = new Map<string, UnmanagedCard[]>();
    unmanaged.forEach((u) => {
      const label = accounts.find((a) => String(a.id) === String(u.account_id))?.label || 'Other account';
      if (!m.has(label)) m.set(label, []);
      m.get(label)!.push(u);
    });
    return Array.from(m.entries()).map(([label, rows]) => ({ label: label as string | null, rows }));
  }, [accounts, unmanaged]);

  if (!canManage) return <Redirect to="/" />;

  return (
    <PageLayout width="narrow" kind="workspace" className="pt-4 lg:pt-6 text-on-surface">
      <>
        <h1 className="flex items-center gap-2.5 text-2xl font-bold mb-1">
          <span className="w-1 h-6 rounded-full bg-primary/60" />
          Company Cards
        </h1>
        <p className="text-sm text-on-surface-variant mb-6 max-w-[60ch]">
          Assign a card to a person and only they (plus Financial users) see it in their receipt dropdown.
          Leave it unassigned to keep it shared with everyone.
          {cards.length > 0 && <span className="text-on-surface-variant"> {ownedCount}/{cards.length} assigned.</span>}
        </p>

        {/* Unmanaged cards — last4s seen on statements that aren't set up yet */}
        <Reveal show={unmanaged.length > 0}>
          <div className="pb-5">
          <Alert
            tone="warning"
            live={false}
            title={
              <span className="inline-flex items-center gap-2">
                Unmanaged cards
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300">{unmanaged.length}</span>
              </span>
            }
          >
            <p className="text-xs text-on-surface-variant mb-3 max-w-[62ch]">
              These card numbers appear on imported statements but aren't set up here, so their charges can't be tied to a person. Set each one up and assign an owner.
            </p>
            <div className="space-y-2.5">
              {unmanagedGroups.map((g) => (
                <div key={g.label || 'all'}>
                  {g.label && <div className="text-xs font-medium text-on-surface mb-1.5">{g.label}</div>}
                  <div className="flex flex-wrap gap-2">
                    {g.rows.map((u) => (
                      <button key={`${u.account_id}-${u.last4}`} onClick={() => setupUnmanaged(u)}
                        className={`${buttonClass('quiet')} group`}>
                        <span className="font-mono tabular-nums text-sm text-on-surface">•• {u.last4}</span>
                        <span className="text-xs text-on-surface-variant">{u.txn_count} charge{u.txn_count === 1 ? '' : 's'}</span>
                        <span className="text-xs font-semibold text-amber-300 group-hover:text-amber-200">Set up</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Alert>
          </div>
        </Reveal>

        {/* Add a card */}
        <Panel label="Add a card" padding="sm" className="mb-5">
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex-1 min-w-[10rem]">
            <span className={fieldLabel}>Label</span>
            <Field ref={labelRef} value={newCard.label} placeholder="Cardholder name, e.g. Jamie"
                   onChange={(e) => setNewCard((n) => ({ ...n, label: e.target.value }))} />
          </label>
          <label className="w-24">
            <span className={fieldLabel}>Last 4</span>
            <Field className="font-mono tabular-nums" value={newCard.last4} placeholder="1234" inputMode="numeric" maxLength={4}
                   onChange={(e) => setNewCard((n) => ({ ...n, last4: e.target.value }))} />
          </label>
          <label className="w-28">
            <span className={fieldLabel}>Network</span>
            <Field value={newCard.network} placeholder="visa"
                   onChange={(e) => setNewCard((n) => ({ ...n, network: e.target.value }))} />
          </label>
          {accounts.length > 0 && (
            <div className="w-40">
              <span className={fieldLabel}>Account</span>
              <Select label="Account" value={String(newCard.account_id ?? '')}
                      onChange={(k) => setNewCard((n) => ({ ...n, account_id: k === '' ? null : Number(k) }))}
                      options={[{ key: '', label: 'No account' }, ...accounts.map((a) => ({ key: String(a.id), label: a.label }))]} />
            </div>
          )}
          <button onClick={addCard} disabled={busy}
                  className={buttonClass('filled')}>
            Add card
          </button>
        </div>
        </Panel>

        {/* Card roster */}
        <FadeIn key={loading ? 'loading' : cards.length === 0 ? 'empty' : 'cards'}>
        {loading ? (
          <SkeletonGroup label="Loading cards" className="space-y-2">
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-[62px] rounded-panel" />)}
          </SkeletonGroup>
        ) : cards.length === 0 ? (
          <EmptyState variant="panel">No cards yet. Add one above.</EmptyState>
        ) : (
          <ListStack
            items={cards}
            getKey={(c) => String(c.id)}
            spacing="loose"
            label="Company cards"
            renderRow={(c) => {
              const editing = editingCard === c.id;
              // Detect user is offered on unowned cards always, and on owned cards
              // only while editing them (so it doesn't clutter the settled roster).
              const showDetect = !c.owner_user_id || editing;
              const detectBtn = showDetect && (
                <button onClick={() => runDetect(c.id)} disabled={detecting === c.id}
                        className={buttonClass('quiet', 'sm')}>
                  {detecting === c.id ? 'Detecting…' : detectFor === c.id ? 'hide' : 'Detect user'}
                </button>
              );
              return (
              <ListRow className={c.is_active ? '' : 'opacity-60'}>
                <div className="flex flex-wrap items-center gap-3 px-1 py-0.5">
                  <FadeIn key={editing ? 'edit' : 'view'} className={editing ? 'min-w-[16rem] flex-1' : 'min-w-0 flex-1'}>
                  {editing ? (
                    <div className="flex-1 min-w-[16rem] flex flex-wrap items-end gap-2">
                      <label className="flex-1 min-w-[10rem]">
                        <span className={fieldLabel}>Label</span>
                        <Field autoFocus value={editDraft.label}
                               onChange={(e) => setEditDraft((d) => ({ ...d, label: e.target.value }))} />
                      </label>
                      <label className="w-24">
                        <span className={fieldLabel}>Last 4</span>
                        <Field className="font-mono tabular-nums" value={editDraft.last4} inputMode="numeric" maxLength={4}
                               onChange={(e) => setEditDraft((d) => ({ ...d, last4: e.target.value }))} />
                      </label>
                      <label className="w-28">
                        <span className={fieldLabel}>Network</span>
                        <Field value={editDraft.network}
                               onChange={(e) => setEditDraft((d) => ({ ...d, network: e.target.value }))} />
                      </label>
                      <button onClick={() => saveEdit(c)} disabled={savingEdit || !editDraft.label.trim()}
                              className={buttonClass('filled', 'sm')}>
                        {savingEdit ? 'Saving…' : 'Save'}
                      </button>
                      <button onClick={() => setEditingCard(null)} disabled={savingEdit}
                              className={buttonClass('quiet', 'sm')}>cancel</button>
                    </div>
                  ) : (
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium truncate" title={c.label}>{formatCardLabel(c)}</span>
                        <button onClick={() => startEdit(c)} className={`${buttonClass('quiet', 'sm')} shrink-0`}>edit</button>
                      </div>
                      <div className="text-xs text-on-surface-variant">{c.last4 ? (c.network || '') : 'no last 4'}</div>
                    </div>
                  )}
                  </FadeIn>

                  {/* Owner */}
                  <div className="min-w-[12rem]">
                    <FadeIn key={editingOwner === c.id ? 'pick' : c.owner_name ? 'owned' : 'shared'}>
                    {editingOwner === c.id ? (
                      <div className="flex items-center gap-2">
                        <div className="flex-1"><UserPicker users={users} onSelect={(u: UserRow) => assignOwner(c.id, u.id)} /></div>
                        <button onClick={() => setEditingOwner(null)} className={buttonClass('quiet', 'sm')}>cancel</button>
                      </div>
                    ) : c.owner_name ? (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="inline-flex items-center truncate max-w-[10rem] rounded-full bg-primary/15 text-primary-ink ring-1 ring-inset ring-primary/30 px-2.5 py-0.5 text-[11px] font-medium">{c.owner_name}</span>
                        <button onClick={() => startOwnerEdit(c.id)} className={buttonClass('quiet', 'sm')}>change</button>
                        <button onClick={() => assignOwner(c.id, null)} className={buttonClass('quiet', 'sm')}>clear</button>
                        {editing && detectBtn}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-xs text-on-surface-variant">Shared with everyone</span>
                        <button onClick={() => startOwnerEdit(c.id)} className={buttonClass('quiet', 'sm')}>assign</button>
                        {detectBtn}
                      </div>
                    )}
                    </FadeIn>
                  </div>

                  {/* Default purchase class — the folder importer derives its auto-class
                      from this (interactive scans pick on the slider instead) */}
                  {classes.length > 0 && (
                    <div className="min-w-[9rem]">
                      <span className={fieldLabel}>Default class</span>
                      <Select
                        label="Default class"
                        size="sm"
                        value={String(c.default_purchase_class_id ?? '')}
                        onChange={(k) => patchCard(c.id, { default_purchase_class_id: k === '' ? null : Number(k) })}
                        options={[{ key: '', label: 'None (Drone Shows)' }, ...classes.map((cl) => ({ key: String(cl.id), label: cl.label }))]} />
                    </div>
                  )}

                  {/* Reconciliation account: which account's statements this card's
                      charges arrive on. Read-only when the accounts list is not ours to load. */}
                  {accounts.length > 0 ? (
                    <div className="min-w-[9rem]">
                      <span className={fieldLabel}>Account</span>
                      <Select
                        label="Account"
                        size="sm"
                        value={String(c.account_id ?? '')}
                        onChange={(k) => patchCard(c.id, { account_id: k === '' ? null : Number(k) })}
                        options={[{ key: '', label: 'No account' }, ...accounts.map((a) => ({ key: String(a.id), label: a.label }))]} />
                    </div>
                  ) : c.account_label ? (
                    <div className="min-w-[9rem]">
                      <span className={fieldLabel}>Account</span>
                      <span className="text-xs text-on-surface">{c.account_label}</span>
                    </div>
                  ) : null}

                  {/* Active toggle */}
                  <Tooltip label={c.is_active ? 'Deactivate (hide from dropdowns)' : 'Reactivate'}>
                    <button onClick={() => patchCard(c.id, { is_active: !c.is_active })}
                            className={`text-[11px] px-2.5 py-0.5 rounded-full ring-1 ring-inset transition shrink-0 ${c.is_active ? 'bg-green-500/15 text-green-300 ring-green-500/30' : 'bg-gray-700/40 text-on-surface-variant ring-outline-variant'}`}>
                      {c.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </Tooltip>
                </div>

                {/* Owner-detection guesses */}
                <Reveal show={detectFor === c.id}>
                  <div className="px-1 pt-2 pb-1">
                    {candidates.length === 0 ? (
                      <p className="text-xs text-on-surface-variant">No good guess. Assign manually.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {candidates.slice(0, 3).map((cand) => {
                          const cap = evidenceCaption(cand.evidence);
                          return (
                            <button key={cand.user_id} onClick={() => applyCandidate(c.id, cand)}
                                    className={`${buttonClass('quiet')} group`}>
                              <span className="text-sm text-on-surface group-hover:text-white">{cand.name}</span>
                              {cap && <span className="text-xs text-on-surface-variant">{cap}</span>}
                              <span className="text-[11px] font-mono tabular-nums text-on-surface-variant">{Math.round(cand.confidence * 100)}%</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </Reveal>
              </ListRow>
              );
            }}
          />
        )}
        </FadeIn>
      </>
    </PageLayout>
  );
};

export default CardManager;
